import Phaser from 'phaser'
import { EVENTS } from '../types/GameTypes'

export class DeathScreen {
  private scene: Phaser.Scene
  private container: Phaser.GameObjects.Container
  private background: Phaser.GameObjects.Rectangle
  private deathText: Phaser.GameObjects.Text
  private respawnButton: Phaser.GameObjects.Container
  private respawnCountdown: number = 5
  private countdownText: Phaser.GameObjects.Text
  private countdownTimer?: Phaser.Time.TimerEvent
  private isVisible: boolean = false

  // Callback for respawn
  public onRespawn: (() => void) | null = null

  constructor(scene: Phaser.Scene) {
    this.scene = scene

    // Create container (initially hidden)
    this.container = scene.add.container(0, 0)
    this.container.setDepth(2000)
    this.container.setVisible(false)

    // Semi-transparent background
    this.background = scene.add.rectangle(
      scene.scale.width / 2,
      scene.scale.height / 2,
      scene.scale.width,
      scene.scale.height,
      0x000000,
      0.7
    )
    this.container.add(this.background)

    // "You Died" text
    this.deathText = scene.add.text(
      scene.scale.width / 2,
      scene.scale.height / 2 - 80,
      'YOU DIED',
      {
        fontSize: '48px',
        color: '#ef4444',
        fontFamily: 'Arial',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }
    )
    this.deathText.setOrigin(0.5)
    this.container.add(this.deathText)

    // Countdown text
    this.countdownText = scene.add.text(
      scene.scale.width / 2,
      scene.scale.height / 2,
      '',
      {
        fontSize: '24px',
        color: '#ffffff',
        fontFamily: 'Arial'
      }
    )
    this.countdownText.setOrigin(0.5)
    this.container.add(this.countdownText)

    // Respawn button
    this.respawnButton = this.createRespawnButton()
    this.container.add(this.respawnButton)

    // Listen for player death
    const gameScene = scene.scene.get('GameScene')
    if (gameScene) {
      gameScene.events.on(EVENTS.PLAYER_DIED, this.show, this)
      gameScene.events.on(EVENTS.PLAYER_RESPAWNED, this.hide, this)
    }

    // Handle resize
    scene.scale.on('resize', this.handleResize, this)
  }

  private createRespawnButton(): Phaser.GameObjects.Container {
    const buttonContainer = this.scene.add.container(
      this.scene.scale.width / 2,
      this.scene.scale.height / 2 + 80
    )

    // Button background
    const buttonBg = this.scene.add.rectangle(0, 0, 200, 50, 0x22c55e)
    buttonBg.setStrokeStyle(2, 0x16a34a)

    // Button text
    const buttonText = this.scene.add.text(0, 0, 'RESPAWN', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })
    buttonText.setOrigin(0.5)

    buttonContainer.add([buttonBg, buttonText])

    // Make interactive
    buttonBg.setInteractive({ useHandCursor: true })
    buttonBg.on('pointerover', () => {
      buttonBg.setFillStyle(0x16a34a)
    })
    buttonBg.on('pointerout', () => {
      buttonBg.setFillStyle(0x22c55e)
    })
    buttonBg.on('pointerdown', () => {
      this.respawn()
    })

    // Initially hidden (shown after countdown)
    buttonContainer.setVisible(false)

    return buttonContainer
  }

  /**
   * Show the death screen
   */
  public show(): void {
    if (this.isVisible) return

    this.isVisible = true
    this.container.setVisible(true)
    this.respawnButton.setVisible(false)
    this.respawnCountdown = 5

    // Update countdown text
    this.updateCountdownText()

    // Start countdown
    this.countdownTimer = this.scene.time.addEvent({
      delay: 1000,
      callback: this.tickCountdown,
      callbackScope: this,
      repeat: 4
    })

    // Fade in
    this.container.setAlpha(0)
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 500
    })
  }

  /**
   * Hide the death screen
   */
  public hide(): void {
    if (!this.isVisible) return

    this.isVisible = false

    // Stop countdown if running
    if (this.countdownTimer) {
      this.countdownTimer.destroy()
      this.countdownTimer = undefined
    }

    // Fade out
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 300,
      onComplete: () => {
        this.container.setVisible(false)
      }
    })
  }

  private tickCountdown(): void {
    this.respawnCountdown--
    this.updateCountdownText()

    if (this.respawnCountdown <= 0) {
      // Show respawn button
      this.respawnButton.setVisible(true)
      this.countdownText.setText('Click to respawn')
    }
  }

  private updateCountdownText(): void {
    if (this.respawnCountdown > 0) {
      this.countdownText.setText(`Respawn in ${this.respawnCountdown}...`)
    }
  }

  private respawn(): void {
    if (this.onRespawn) {
      this.onRespawn()
    }
    this.hide()
  }

  private handleResize(): void {
    const width = this.scene.scale.width
    const height = this.scene.scale.height

    // Update background size
    this.background.setPosition(width / 2, height / 2)
    this.background.setSize(width, height)

    // Update text positions
    this.deathText.setPosition(width / 2, height / 2 - 80)
    this.countdownText.setPosition(width / 2, height / 2)
    this.respawnButton.setPosition(width / 2, height / 2 + 80)
  }

  /**
   * Clean up
   */
  public destroy(): void {
    const gameScene = this.scene.scene.get('GameScene')
    if (gameScene) {
      gameScene.events.off(EVENTS.PLAYER_DIED, this.show, this)
      gameScene.events.off(EVENTS.PLAYER_RESPAWNED, this.hide, this)
    }

    if (this.countdownTimer) {
      this.countdownTimer.destroy()
    }

    this.container.destroy()
  }
}
